import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerFinancialEntitiesRoute,
  leagueCustomerFinancialEntityIndividualsRoute,
  leagueCustomerFinancialEntityRoute,
  leagueCustomerFinancialSiloBalancesRoute,
  leagueCustomerRosterMemberRoute,
  leagueCustomerTeamRoute,
} from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import countriesData from "constants/countries.json";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { teamImageUrl } from "./team-image-helpers";
import {
  buildPlayerFinanceDescriptor,
  buildTeamFinanceDescriptor,
  playerDisplayName,
} from "./financial/finance-descriptors";
import { formatFinancialAmount } from "./financial/financial-helpers";
import { accountFinanceRoutes } from "./player-financials/account-finance-routes";
import AccountFinanceSection from "./player-financials/AccountFinanceSection";
import CreateMembershipDialog from "./player-financials/CreateMembershipDialog";
import LifetimeToggleButton from "./player-financials/LifetimeToggleButton";
import MembershipStatusActions from "./player-financials/MembershipStatusActions";
import { memberSinceDetail } from "./player-financials/member-since";
import PlayerFormDialog from "./PlayerFormDialog";
import { membershipChipColor, membershipChipTooltip } from "utils/membership-ui-helpers";
import { suspensionChipLabel, suspensionTooltip } from "utils/suspension-ui-helpers";

const accountKind = (params) => {
  if (params.memberId) return "player";
  if (params.teamId) return "team";
  if (params.individualId) return "individual";
  return "entity";
};

const ProfileField = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <RevaText variant="caption" color="text.secondary">
      {label}
    </RevaText>
    <RevaText variant="body1">{children || "—"}</RevaText>
  </Grid>
);

// Calendar dates arrive as UTC midnight; parsing them in local time shifts them to the previous day.
const calendarDate = (value) => (value ? dayjs(String(value).slice(0, 10)) : null);
const formatDate = (value) => calendarDate(value)?.format("DD/MM/YYYY") ?? null;

const MemberSince = ({ customerId, membership, intl, onChanged }) => {
  if (!membership) return "—";
  const detail = memberSinceDetail(membership.lifetime);
  return (
    <>
      {formatDate(membership.started_at) || "—"}
      {detail?.years && (
        <RevaText component="span" variant="body2" color="text.secondary">
          {" · "}
          {intl.formatMessage({ id: detail.years.id }, detail.years.values)}
        </RevaText>
      )}
      {detail?.lifetime && (
        <RevaText
          component="span"
          variant="body2"
          color={membership.lifetime.is_lifetime ? "primary" : "text.secondary"}
          sx={{ display: "block" }}
        >
          {intl.formatMessage({ id: detail.lifetime.id }, detail.lifetime.values)}
        </RevaText>
      )}
      <LifetimeToggleButton customerId={customerId} membership={membership} onChanged={onChanged} />
    </>
  );
};

const PlayerProfile = ({ customerId, player, intl, onChanged }) => {
  const country = countriesData.find((entry) => entry.code === player.document_nationality);
  const membership = player.membership;
  const [creatingMembership, setCreatingMembership] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(false);
  const canCreateMembership = !membership && !player.archived;
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar src={player.profile_pic_url || undefined} sx={{ width: 72, height: 72 }}>
          {[player.first_name?.[0], player.last_name?.[0]].filter(Boolean).join("").toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <RevaText variant="h5">{playerDisplayName(player)}</RevaText>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            {player.archived && (
              <Chip size="small" label={intl.formatMessage({ id: "gestion.accountFinance.archived" })} />
            )}
            <Chip
              size="small"
              variant="outlined"
              color={player.linked_user ? "success" : "default"}
              label={intl.formatMessage({
                id: player.linked_user ? "gestion.players.associatedToUser" : "gestion.players.notAssociatedToUser",
              })}
            />
            {player.affiliation ? (
              // X-07: the computed status (e.g. "Moroso"), the same one the planillero shows.
              <Tooltip title={membershipChipTooltip(player.affiliation)}>
                <Chip
                  data-testid="player-affiliation"
                  size="small"
                  variant="outlined"
                  color={membershipChipColor(player.affiliation)}
                  label={intl.formatMessage(
                    { id: "gestion.accountFinance.membershipStatus" },
                    { status: player.affiliation.label },
                  )}
                />
              </Tooltip>
            ) : membership && (
              <Chip
                size="small"
                variant="outlined"
                color={membership.status === "active" ? "primary" : "default"}
                label={intl.formatMessage(
                  { id: "gestion.accountFinance.membershipStatus" },
                  {
                    status: intl.formatMessage({
                      id: `org.membership.status.${membership.status}`,
                      defaultMessage: membership.status,
                    }),
                  },
                )}
              />
            )}
            {canCreateMembership && !player.affiliation && (
              <Chip size="small" variant="outlined" label={intl.formatMessage({ id: "gestion.accountFinance.noMembership" })} />
            )}
            {(player.active_suspensions || []).length > 0 && (
              <Tooltip title={suspensionTooltip(player.active_suspensions)}>
                <Chip
                  data-testid="player-suspensions"
                  size="small"
                  color="error"
                  variant="outlined"
                  label={suspensionChipLabel(intl, player.active_suspensions)}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditingPlayer(true)}>
            {intl.formatMessage({ id: "gestion.edit" })}
          </Button>
          {membership && !player.archived && (
            <MembershipStatusActions customerId={customerId} membership={membership} onChanged={onChanged} />
          )}
          {canCreateMembership && (
            <Button variant="contained" size="small" onClick={() => setCreatingMembership(true)}>
              {intl.formatMessage({ id: "gestion.accountFinance.createMembership" })}
            </Button>
          )}
        </Stack>
      </Stack>
      <PlayerFormDialog
        open={editingPlayer}
        customerId={customerId}
        player={player}
        onClose={() => setEditingPlayer(false)}
        onChanged={onChanged}
      />
      {canCreateMembership && (
        <CreateMembershipDialog
          customerId={customerId}
          player={player}
          open={creatingMembership}
          onClose={() => setCreatingMembership(false)}
          onCreated={() => {
            setCreatingMembership(false);
            onChanged();
          }}
        />
      )}
      <Divider />
      <Grid container spacing={2}>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.document" })}>
          {player.document_number
            ? [country ? `${country.flag} ${country.code}` : player.document_nationality, player.document_number]
                .filter(Boolean)
                .join(" ")
            : null}
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.memberNumber" })}>
          {player.member_number}
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.accountFinance.memberSince" })}>
          <MemberSince customerId={customerId} membership={membership} intl={intl} onChanged={onChanged} />
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.phone" })}>{player.phone_number}</ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.email" })}>{player.email}</ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.revaUser" })}>
          {player.linked_user?.full_name}
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.medicalRecord" })}>
          {player.medical_record_url ? (
            <Link href={player.medical_record_url} target="_blank" rel="noopener noreferrer">
              {intl.formatMessage({ id: "gestion.players.viewMedicalRecord" })}
            </Link>
          ) : null}
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.players.medicalRecordExp" })}>
          {formatDate(player.medical_record_expires_at)}
        </ProfileField>
        <ProfileField label={intl.formatMessage({ id: "gestion.accountFinance.teams" })}>
          {player.teams?.length ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap component="span">
              {player.teams.map((team) => (
                <Chip
                  key={team.id}
                  size="small"
                  label={team.name}
                  component={RouterLink}
                  to={GESTION_ROUTES.teamFinancials(customerId, team.id)}
                  clickable
                />
              ))}
            </Stack>
          ) : null}
        </ProfileField>
      </Grid>
    </Stack>
  );
};

const TeamProfile = ({ customerId, team, intl }) => {
  const players = team.players ?? [];
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          src={teamImageUrl(team, "medium") || undefined}
          variant="rounded"
          sx={{ width: 72, height: 72, bgcolor: "action.hover" }}
        >
          {team.short_name?.slice(0, 2)?.toUpperCase() || "?"}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <RevaText variant="h5">{team.name}</RevaText>
          <RevaText variant="body2" color="text.secondary">
            {team.short_name || "—"}
          </RevaText>
        </Box>
        <Button component={RouterLink} to={GESTION_ROUTES.team(customerId, team.id)} variant="outlined" size="small">
          {intl.formatMessage({ id: "gestion.teamDetail.view" })}
        </Button>
      </Stack>
      <Divider />
      <Grid container spacing={2}>
        <ProfileField label={intl.formatMessage({ id: "gestion.accountFinance.playerCount" })}>
          {String(players.length)}
        </ProfileField>
        {team.archived && (
          <ProfileField label={intl.formatMessage({ id: "gestion.players.status" })}>
            {intl.formatMessage({ id: "gestion.accountFinance.archived" })}
          </ProfileField>
        )}
      </Grid>
      {players.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {players.map((player) => {
            const member = player.playerable || player.roster_member;
            const label = [player.shirt_number != null ? `#${player.shirt_number}` : null, member ? playerDisplayName(member) : player.name]
              .filter(Boolean)
              .join(" ");
            return member?.id ? (
              <Chip
                key={player.id}
                size="small"
                label={label}
                component={RouterLink}
                to={GESTION_ROUTES.playerFinancials(customerId, member.id)}
                clickable
              />
            ) : (
              <Chip key={player.id} size="small" label={label} />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const GenericProfile = ({ name, targetId }) => (
  <Stack spacing={0.5}>
    <RevaText variant="h5">{name || targetId}</RevaText>
    <RevaText variant="body2" color="text.secondary">
      {targetId}
    </RevaText>
  </Stack>
);

const GestionAccountFinancePage = () => {
  const params = useParams();
  const kind = accountKind(params);
  const { customerId } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const entityId = params.entityId ? decodeURIComponent(params.entityId) : null;
  const individualId = params.individualId ? decodeURIComponent(params.individualId) : null;

  const routes = useMemo(
    () => (customerId && account ? accountFinanceRoutes(customerId, account) : null),
    [account, customerId],
  );

  // prepare re-runs when user/intl change; only the latest run may write state, otherwise a
  // slower earlier run can replace a freshly created account with an error.
  const prepareRunRef = useRef(0);

  const prepare = useCallback(async () => {
    if (!customerId || !user) return;
    const runId = ++prepareRunRef.current;
    const isLatest = () => runId === prepareRunRef.current;
    setLoading(true);
    setError(null);
    setAccount(null);
    setProfile(null);
    try {
      if (kind === "player") {
        const { data: player } = await performRequest(
          "GET",
          leagueCustomerRosterMemberRoute(customerId, params.memberId),
          {},
          true,
          user,
        );
        const descriptor = buildPlayerFinanceDescriptor(player);
        const { data: individual } = await performRequest(
          "POST",
          leagueCustomerFinancialEntityIndividualsRoute(customerId, descriptor.parentEntityId),
          descriptor.payload,
          true,
          user,
        );
        if (isLatest()) setProfile(player);
        if (isLatest()) setAccount({ ...descriptor.account, currency: individual?.account?.currency_code || "" });
        return;
      }

      if (kind === "team") {
        const { data: team } = await performRequest(
          "GET",
          leagueCustomerTeamRoute(customerId, params.teamId),
          {},
          true,
          user,
        );
        const descriptor = buildTeamFinanceDescriptor({ team });
        let entity;
        try {
          ({ data: entity } = await performRequest(
            "GET",
            leagueCustomerFinancialEntityRoute(customerId, descriptor.externalEntityId),
            {},
            true,
            user,
          ));
        } catch (lookupError) {
          if (lookupError?.response?.status !== 404) throw lookupError;
          ({ data: entity } = await performRequest(
            "POST",
            leagueCustomerFinancialEntitiesRoute(customerId),
            descriptor.payload,
            true,
            user,
          ));
        }
        if (isLatest()) setProfile(team);
        if (isLatest()) setAccount({
          type: "entity",
          entityId: descriptor.externalEntityId,
          individualId: null,
          currency: entity?.account?.currency_code || "",
          phoneNumber: null,
          supportsMembership: false,
        });
        return;
      }

      const { data: accounts } = await performRequest(
        "GET",
        leagueCustomerFinancialSiloBalancesRoute(customerId),
        { external_entity_id: entityId },
        true,
        user,
      );
      const targetId = kind === "individual" ? individualId : entityId;
      const match = (Array.isArray(accounts) ? accounts : []).find(
        (entry) => entry.type === kind && String(entry.target_id) === String(targetId),
      );
      if (!match) {
        if (isLatest()) setError(intl.formatMessage({ id: "gestion.accountFinance.notFound" }));
        return;
      }
      if (isLatest()) setProfile({ name: match.target_name, targetId });
      if (isLatest()) setAccount({
        type: kind,
        entityId,
        individualId: kind === "individual" ? individualId : null,
        currency: match.currency_code || "",
        phoneNumber: match.contact_info?.phone_number || match.contact_info?.phone || null,
        supportsMembership: false,
      });
    } catch (requestError) {
      if (isLatest()) setError(
        requestError?.response?.status === 404
          ? intl.formatMessage({ id: "gestion.accountFinance.notFound" })
          : requestError?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }),
      );
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [customerId, entityId, individualId, intl, kind, params.memberId, params.teamId, user]);

  useEffect(() => {
    prepare();
  }, [prepare]);

  const loadBalance = useCallback(() => {
    if (!routes?.balance || !user) return;
    performRequest("GET", routes.balance, {}, true, user)
      .then((response) => setBalance(response.data))
      .catch(() => setBalance(null));
  }, [routes, user]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  // F-10: re-read the header (affiliation, suspensions) without resetting the account section.
  const refreshProfile = useCallback(async () => {
    if (kind !== "player" || !customerId || !user || !params.memberId) return;
    const runId = prepareRunRef.current;
    try {
      const { data } = await performRequest(
        "GET",
        leagueCustomerRosterMemberRoute(customerId, params.memberId),
        {},
        true,
        user,
      );
      if (runId === prepareRunRef.current && data) setProfile(data);
    } catch {
      // keep the current header; the next full load will fix it
    }
  }, [customerId, kind, params.memberId, user]);

  const handleAccountUpdated = useCallback(() => {
    loadBalance();
    refreshProfile();
  }, [loadBalance, refreshProfile]);

  if (!customerId) return null;

  const backTo =
    kind === "player"
      ? GESTION_ROUTES.players(customerId)
      : kind === "team"
        ? GESTION_ROUTES.teams(customerId)
        : GESTION_ROUTES.financials(customerId);
  const backLabelId =
    kind === "player"
      ? "gestion.accountFinance.backToPlayers"
      : kind === "team"
        ? "gestion.teamDetail.backToTeams"
        : "gestion.accountFinance.backToFinancials";
  const currency = balance?.currency || account?.currency || "";
  const startYear = calendarDate(profile?.membership?.started_at)?.year() ?? null;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton component={RouterLink} to={backTo} size="small">
            <ArrowBackIcon />
          </IconButton>
          <RevaText variant="h4" sx={{ flex: 1 }}>
            {intl.formatMessage({ id: "gestion.players.financials" })}
          </RevaText>
          <Button component={RouterLink} to={backTo} variant="outlined">
            {intl.formatMessage({ id: backLabelId })}
          </Button>
        </Stack>

        {loading && (
          <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && profile && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
            <Card variant="outlined" sx={{ flex: 3 }}>
              <CardContent>
                {kind === "player" && (
                  <PlayerProfile customerId={customerId} player={profile} intl={intl} onChanged={prepare} />
                )}
                {kind === "team" && <TeamProfile customerId={customerId} team={profile} intl={intl} />}
                {kind !== "player" && kind !== "team" && (
                  <GenericProfile name={profile.name} targetId={profile.targetId} />
                )}
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: 1, minWidth: 220 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <RevaText variant="overline" color="text.secondary">
                      {intl.formatMessage({ id: "gestion.accountFinance.balance" })}
                    </RevaText>
                    <RevaText
                      variant="h5"
                      color={Number(balance?.available ?? 0) < 0 ? "error.main" : undefined}
                    >
                      {balance ? formatFinancialAmount(balance.available, currency) : "—"}
                    </RevaText>
                  </Box>
                  <Box>
                    <RevaText variant="overline" color="text.secondary">
                      {intl.formatMessage({ id: "gestion.accountFinance.futureCharges" })}
                    </RevaText>
                    <RevaText variant="body1">
                      {balance ? formatFinancialAmount(balance.pending, currency) : "—"}
                    </RevaText>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {!loading && account && routes && (
          <AccountFinanceSection
            customerId={customerId}
            account={account}
            availableBalance={balance?.available ?? null}
            currency={currency}
            routes={routes}
            startYear={startYear}
            onUpdated={handleAccountUpdated}
          />
        )}
      </Stack>
    </Box>
  );
};

export default GestionAccountFinancePage;
